/**
 * A plan's name in the client's words (UX review, 17 Oct 2026).
 *
 * The engine names a row for itself — "Pay 40% RAD, Keep Home + Clear Debt (Bank -> ABP)". A family
 * member reads that as code. The same row, said the way an adviser would across the table:
 *
 *   Pay $320,000 up front, keep the home
 *   40% of the room as a refundable deposit; $480,000 paid daily, at $112 a day. Kerry stays in the home.
 *
 * Nothing here is re-derived: the deposit, the room price and the daily payment are the row's own
 * figures. Rows this file does not understand (a move between homes, a village exit, two rooms)
 * fall back to the display name the engine already produces for them, so nothing is ever named wrong.
 */
import { formatCurrency } from '../../utils/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

export interface PlanRowForWords {
    name?: string;
    radPaidNet?: unknown;
    radRoomPrice?: unknown;
    strategySpecificAgedCareCosts?: { dailyAccommodationPayment?: number } | Record<string, unknown>;
    strategyOptions?: { downsizeHome?: boolean; rentHome?: boolean; sellHome?: boolean } | Record<string, unknown>;
    meansTestStrategy?: { purchaseAmount?: number } | null;
    equityRelease?: { kind?: string; loanAmount?: number } | null;
    facilityMove?: unknown;
    villageExit?: unknown;
    secondRoom?: unknown;
}

export interface PlanWords {
    /** "Pay $320,000 up front, keep the home" */
    title: string;
    /** "40% of the room as a refundable deposit; $480,000 paid daily, at $112 a day. Kerry stays in the home." */
    sub: string;
    /** True when the row was named by the engine's own display name instead (a move, a village exit, two rooms). */
    fallback: boolean;
}

const isBaseline = (name: string) => /^do nothing\b/i.test(name) || /^pay the dap\b/i.test(name);

/** What the plan does with the family home, read off the engine's name and options. */
export function homeClause(row: PlanRowForWords, factFind: DraftFactFind | null | undefined): string | null {
    const name = String(row.name || '');
    const opts = (row.strategyOptions || {}) as { downsizeHome?: boolean; rentHome?: boolean; sellHome?: boolean };
    const ownsHome = (Number(factFind?.home?.value) || 0) > 0;
    if (opts.downsizeHome || /downsiz/i.test(name)) return 'downsize the home';
    if (opts.rentHome || /rent home|rent the home|rent out/i.test(name)) return 'rent out the home';
    if (opts.sellHome || /\bsell\b/i.test(name)) return 'sell the home';
    if (/keep home|keep the home/i.test(name)) return 'keep the home';
    return ownsHome ? 'keep the home' : null;
}

/** The extras a name can carry — a debt cleared, a lifetime income bought, money borrowed against the home. */
function extrasClause(row: PlanRowForWords): string {
    const name = String(row.name || '');
    const parts: string[] = [];
    if (/clear debt/i.test(name)) parts.push('clear the debt');
    else if (/pay down debt/i.test(name)) parts.push('pay down the debt');
    if ((Number(row.meansTestStrategy?.purchaseAmount) || 0) > 0) parts.push('buy a guaranteed income for life');
    const er = row.equityRelease;
    if (er && (Number(er.loanAmount) || 0) > 0) parts.push(er.kind === 'heas' ? 'borrow through the Home Equity Access Scheme' : 'borrow against the home');
    return parts.length ? `, ${parts.join(', ')}` : '';
}

/**
 * A SAVED plan's title: the words it was saved with, or — for a plan saved before the words existed —
 * the same words worked out from its engine row now, so Home and the landing never show "Pay 40% RAD".
 */
export function savedPlanTitle(plan: { displayName?: unknown; name?: unknown; strategy?: unknown } | null | undefined, factFind: DraftFactFind | null | undefined): string {
    const saved = String(plan?.displayName || '').trim();
    if (saved) return saved;
    const row = plan?.strategy as PlanRowForWords | undefined;
    const engineName = String(plan?.name || row?.name || 'Plan');
    if (!row) return engineName;
    const w = planWords(row, factFind, engineName);
    return w.title;
}

export function planWords(row: PlanRowForWords | null | undefined, factFind: DraftFactFind | null | undefined, fallbackTitle: string): PlanWords {
    if (!row) return { title: fallbackTitle, sub: '', fallback: true };
    const name = String(row.name || '');
    const room = Number(row.radRoomPrice) || 0;
    const paid = Math.max(0, Number(row.radPaidNet) || 0);
    const complex = !!row.facilityMove || !!row.villageExit || !!row.secondRoom;
    if (complex || !(room > 0)) return { title: fallbackTitle, sub: '', fallback: true };

    const dap = Number((row.strategySpecificAgedCareCosts as { dailyAccommodationPayment?: number } | undefined)?.dailyAccommodationPayment) || 0;
    const unpaid = Math.max(0, room - paid);
    const home = homeClause(row, factFind);
    const extras = extrasClause(row);
    const partner = factFind?.adviceFor === 'couple' ? String(factFind?.partner?.firstName || '').trim() : '';
    const partnerStays = partner && home === 'keep the home' && (factFind?.goals as { aged_care_for?: string } | undefined)?.aged_care_for !== 'both';

    if (isBaseline(name)) {
        return {
            title: 'Pay the whole room daily',
            sub: `Nothing paid up front and nothing sold; the ${formatCurrency(room)} room is charged daily${dap > 0 ? `, at ${formatCurrency(Math.round(dap))} a day` : ''}.`,
            fallback: false,
        };
    }

    const pct = Math.round((paid / room) * 100);
    const fullyPaid = unpaid < 0.5;
    const er = row.equityRelease;
    const borrowed = er && (Number(er.loanAmount) || 0) > 0 ? Number(er.loanAmount) : 0;
    // A borrowing plan is named by the borrowing: "Borrow $800,000 against the home, pay the whole room up front".
    const title = borrowed > 0 && paid > 0.5
        ? `Borrow ${formatCurrency(Math.round(borrowed))} ${er?.kind === 'heas' ? 'through the Home Equity Access Scheme' : 'against the home'}, ${fullyPaid ? 'pay the whole room up front' : `pay ${formatCurrency(Math.round(paid))} up front`}${/clear debt/i.test(name) ? ', clear the debt' : ''}`
        : paid > 0.5
            ? `${fullyPaid ? 'Pay the whole room up front' : `Pay ${formatCurrency(Math.round(paid))} up front`}${home ? `, ${home}` : ''}${extras}`
            : `Pay the room daily${home ? `, ${home}` : ''}${extras}`;
    const depositBit = paid > 0.5
        ? (fullyPaid ? 'The whole room as a refundable deposit' : `${pct}% of the room as a refundable deposit`)
        : 'Nothing paid up front';
    const dailyBit = fullyPaid ? '' : `; ${formatCurrency(Math.round(unpaid))} paid daily${dap > 0 ? `, at ${formatCurrency(Math.round(dap))} a day` : ''}`;
    const sub = `${depositBit}${dailyBit}.${partnerStays ? ` ${partner} stays in the home.` : ''}`;
    return { title, sub, fallback: false };
}
